import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, IconTooltip, Tag, TaptapList } from 'sonata-react-components';

import { sortRows } from '../Inventory.helpers';
import { InventoryListSort } from './InventoryListSort';

import type {
  AppSiteMarketplaceType,
  InventoryOperation,
  InventoryRow,
  SortDirection
} from '../../AppAndSiteMarketplace.types';

type RowItem = {
  id: string;
  /** Number of keys, shown as "N found" in search entries. */
  foundCount?: number;
  operation: InventoryOperation;
  url: string;
};

type Props = {
  /** Ids of the rows with a key also on the opposite list (shown as a warning). Only used in the included column (exclusions take priority). */
  conflictIds?: Set<string>;
  /** Id prefix used to build the ids of the inner elements. */
  id: string;
  /** Rows to show (already filtered by the search). */
  rows: InventoryRow[];
  /** Whether a search is active, to show the "no results" empty state. */
  isSearching: boolean;
  onDeleteItem: (operation: InventoryOperation, url: string) => void;
  /** Receives the ids selected in this column. */
  onSelectionChange: (selectedIds: string[]) => void;
  operation: InventoryOperation;
  /** Selected ids of this column. */
  selectedList: string[];
  /** Inventory type, used to word the conflict message. */
  type: AppSiteMarketplaceType;
};

const COLUMN_ICON: Record<InventoryOperation, string> = {
  exclude: 'fa-regular fa-circle-xmark',
  include: 'fa-regular fa-circle-check'
};

/** One column (included or excluded) of the inventory list: header, sort and virtualized rows. */
export function InventoryListColumn({
  conflictIds,
  id,
  isSearching,
  rows: inventoryRows,
  onDeleteItem,
  onSelectionChange,
  operation,
  selectedList,
  type
}: Props) {
  const { formatMessage } = useIntl();
  const [sortDirection, setSortDirection] = useState<SortDirection>();
  const isEmpty = inventoryRows.length === 0;

  const conflictMessage =
    type === 'generic'
      ? formatMessage({
          defaultMessage:
            'This domain is also on your exclusion list. Exclusions take priority, so no bids will be placed for this inventory.',
          id: 'appAndSiteMarketplace.inventory.list.conflict.generic'
        })
      : formatMessage({
          defaultMessage:
            'This key is also on your exclusion list. Exclusions take priority, so no bids will be placed for this inventory.',
          id: 'appAndSiteMarketplace.inventory.list.conflict.specific'
        });

  const conflictHeaderMessage =
    type === 'generic'
      ? formatMessage({
          defaultMessage:
            'Some items of this list are also on your exclusion list. Exclusions take priority, so no bids will be placed for them.',
          id: 'appAndSiteMarketplace.inventory.list.conflictHeader.generic'
        })
      : formatMessage({
          defaultMessage:
            'Some keys of this list are also on your exclusion list. Exclusions take priority, so no bids will be placed for them.',
          id: 'appAndSiteMarketplace.inventory.list.conflictHeader.specific'
        });
  // `conflictIds` is computed on the whole lists, so the header warns even if a search hides the conflicting rows
  const hasConflicts = (conflictIds?.size ?? 0) > 0;

  const rows: RowItem[] = useMemo(
    () =>
      sortRows(inventoryRows, sortDirection).map(row => ({
        foundCount: row.isEntry ? row.keys.length : undefined,
        id: row.id,
        operation,
        url: row.label
      })),
    [inventoryRows, sortDirection, operation]
  );

  const selectedRows = useMemo(() => {
    const selectedSet = new Set(selectedList);
    return rows.filter(row => selectedSet.has(row.id));
  }, [rows, selectedList]);

  return (
    <div className="inventoryList__column">
      <div
        className={`inventoryList__columnHeader inventoryList__columnHeader--${operation}${isEmpty ? ' inventoryList__columnHeader--disabled' : ''}`}
      >
        <span className="inventoryList__columnHeaderTitle">
          <i className={COLUMN_ICON[operation]} />
          {operation === 'include'
            ? formatMessage({
                defaultMessage: 'Included',
                id: 'appAndSiteMarketplace.inventory.list.included'
              })
            : formatMessage({
                defaultMessage: 'Excluded',
                id: 'appAndSiteMarketplace.inventory.list.excluded'
              })}
          {hasConflicts && (
            <span className="inventoryList__columnHeaderConflict">
              <IconTooltip
                customClassName={`inventoryListConflictHeader-${id}`}
                icon="fa-regular fa-triangle-exclamation"
                iconColor="var(--color-status-warning-600) !important"
                place="top"
                portal
              >
                {conflictHeaderMessage}
              </IconTooltip>
            </span>
          )}
        </span>
        <InventoryListSort
          disabled={isEmpty}
          id={id}
          onChange={setSortDirection}
          operation={operation}
          value={sortDirection}
        />
      </div>
      <TaptapList
        className="inventoryListColumnList"
        data={rows}
        defaultSelectedItems={selectedRows}
        id={id}
        onSelectionChange={selected => onSelectionChange((selected as RowItem[]).map(row => row.id))}
        selectionType="rowClick"
      >
        <TaptapList.RowsListVirtualized<RowItem>
          emptyStateDescription={
            isSearching
              ? formatMessage({
                  defaultMessage: 'Try adjusting your search or filters to find items.',
                  id: 'appAndSiteMarketplace.inventory.emptyState.description'
                })
              : formatMessage({
                  defaultMessage: 'Enter or upload your list on the left, and results will appear here automatically.',
                  id: 'appAndSiteMarketplace.inventory.emptyDescription'
                })
          }
          emptyStateIcon="fal fa-empty-set"
          emptyStateTitle={
            isSearching
              ? formatMessage({
                  defaultMessage: 'No results match your filters',
                  id: 'appAndSiteMarketplace.inventory.emptyState.title'
                })
              : formatMessage({
                  defaultMessage: 'Looks like your list is empty',
                  id: 'appAndSiteMarketplace.inventory.emptyTitle'
                })
          }
          estimateSize={44}
        >
          {listItems =>
            listItems.map(item => (
              <TaptapList.RowsListVirtualized.Row id={item.id} key={item.id} virtualData={item.virtualData}>
                {conflictIds?.has(item.id) ? (
                  <span className="inventoryList__rowConflict">
                    <IconTooltip
                      customClassName={`inventoryListConflict-${id}`}
                      icon="fa-regular fa-triangle-exclamation"
                      iconColor="var(--color-status-warning-600) !important"
                      place="top"
                      portal
                    >
                      {conflictMessage}
                    </IconTooltip>
                  </span>
                ) : (
                  <i
                    className={`inventoryList__rowIcon inventoryList__rowIcon--${item.operation} ${COLUMN_ICON[item.operation]}`}
                  />
                )}
                <span className="inventoryList__rowUrl" title={item.url}>
                  {item.url}
                </span>
                {item.foundCount !== undefined && (
                  <Tag
                    className="inventoryList__rowFound"
                    id={`found-${operation}-${item.id}`}
                    rounded
                    status="category"
                    tiny
                  >
                    {formatMessage(
                      {
                        defaultMessage: '{count} found',
                        id: 'appAndSiteMarketplace.inventory.list.found'
                      },
                      { count: item.foundCount }
                    )}
                  </Tag>
                )}
                <ButtonUI
                  className="inventoryList__rowDelete"
                  icon="fa-regular fa-trash"
                  id={`deleteAction-${item.url}`}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    event.stopPropagation();
                    onDeleteItem(item.operation, item.id);
                  }}
                  shape="link"
                  size="ultraSmall"
                  type="secondary"
                />
              </TaptapList.RowsListVirtualized.Row>
            ))
          }
        </TaptapList.RowsListVirtualized>
      </TaptapList>
    </div>
  );
}
